import { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useTranslation } from "../../context/TranslationContext";

// Helper to convert string or Date to Date object (handles DD-MM-YYYY, MM-DD-YYYY, YYYY-MM-DD, etc.)
const toDateSafe = (date, fallback) => {
  if (date instanceof Date) {
    if (!isNaN(date.getTime())) return date;
    // Invalid Date object — fall through to fallback
  }
  if (typeof date === 'string') {
    const normalized = date.replace(/[/.]/g, '-');
    const parts = normalized.split('-').filter(Boolean);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD (unambiguous)
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        const first = parseInt(parts[0]);
        const second = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        // Smart detection: if first > 12 → DD-MM, if second > 12 → MM-DD
        if (first > 12) {
          return new Date(year, second - 1, first); // DD-MM-YYYY
        } else if (second > 12) {
          return new Date(year, first - 1, second); // MM-DD-YYYY
        } else {
          return new Date(year, first - 1, second); // MM-DD-YYYY default
        }
      }
    }
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  if (fallback) return fallback;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// `onApply` runs the search as the calendar closes. Picking dates and then
// having to find "Check Availability" was two steps for one intention — the
// guest has already told us what they want by choosing the dates.
export default function DataComponent({ selectedDates, setSelectedDates, openSignal = 0, onApply }) {
  const { t } = useTranslation();
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  // Externally-triggered open (e.g. "Change Dates" button on the no-rooms card).
  // Open ONLY when openSignal actually increments — not on mount/remount (which
  // would otherwise re-open the calendar on every "Check Availability" search).
  const prevOpenSignal = useRef(openSignal);
  useEffect(() => {
    if (openSignal !== prevOpenSignal.current) {
      prevOpenSignal.current = openSignal;
      if (openSignal > 0) setShowCalendar(true);
    }
  }, [openSignal]);

  // Get calendar selection color from CSS variable
  const [calendarColor, setCalendarColor] = useState('#b02e36');

  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const primaryColor = rootStyles.getPropertyValue('--primary-color').trim();
    if (primaryColor) {
      setCalendarColor(primaryColor);
    }
  }, []);

  // Set default dates: today for check-in, tomorrow for check-out
  useEffect(() => {
    // Helper to create date at midnight (no timezone issues)
    const createDateAtMidnight = (date) => {
      if (date instanceof Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }
      if (typeof date === 'string') {
        const normalized = date.replace(/[/.]/g, '-');
        const parts = normalized.split('-').filter(Boolean);
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            // YYYY-MM-DD (unambiguous)
            return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          } else {
            const first = parseInt(parts[0]);
            const second = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            if (first > 12) {
              return new Date(year, second - 1, first); // DD-MM-YYYY
            } else if (second > 12) {
              return new Date(year, first - 1, second); // MM-DD-YYYY
            } else {
              return new Date(year, first - 1, second); // MM-DD-YYYY default
            }
          }
        }
        return new Date(date);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const checkIn = selectedDates.checkIn ? createDateAtMidnight(selectedDates.checkIn) : today;
    const checkOut = selectedDates.checkOut ? createDateAtMidnight(selectedDates.checkOut) : tomorrow;
    
    if (!checkIn || !checkOut || 
        (checkIn.getTime() !== (selectedDates.checkIn instanceof Date ? selectedDates.checkIn.getTime() : 0)) ||
        (checkOut.getTime() !== (selectedDates.checkOut instanceof Date ? selectedDates.checkOut.getTime() : 0))) {
      setSelectedDates({
        checkIn: checkIn || today,
        checkOut: checkOut || tomorrow,
      });
    }
  }, [selectedDates, setSelectedDates]); // Run when selectedDates change

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCalendar &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  return (
    <div className="lg:col-span-1 relative">
      <label className="block text-xs font-medium text-gray-700 mb-1.5 text-white tracking-wide">
        {t('search.checkInCheckOut')}
      </label>

      {/* Input Field */}
      <input
        type="text"
        readOnly
        value={
          (() => {
            const checkIn = toDateSafe(selectedDates.checkIn);
            const checkOut = toDateSafe(selectedDates.checkOut);
            try {
              return `${format(checkIn, "dd/MM/yyyy")} - ${format(checkOut, "dd/MM/yyyy")}`;
            } catch {
              return '';
            }
          })()
        }
        onClick={() => setShowCalendar(!showCalendar)}
        className="h-10 w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 transition-all duration-200 cursor-pointer"
      />

      {/* Calendar Popup */}
      {showCalendar && (
        <div
          ref={calendarRef}
          className="absolute top-full left-0 mt-2 z-[9999] bg-white shadow-2xl rounded-xl border border-gray-100 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-auto"
        >
          <DateRange
            ranges={[
              {
                startDate: toDateSafe(selectedDates.checkIn),
                endDate: (() => {
                  const tomorrow = new Date();
                  tomorrow.setHours(0, 0, 0, 0);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  return toDateSafe(selectedDates.checkOut, tomorrow);
                })(),
                key: "selection",
              },
            ]}
            onChange={(ranges) => {
              const newCheckIn = ranges.selection.startDate;
              let newCheckOut = ranges.selection.endDate;
              // Checkout must always be at least 1 day after checkin
              if (newCheckOut <= newCheckIn) {
                newCheckOut = new Date(newCheckIn);
                newCheckOut.setDate(newCheckOut.getDate() + 1);
              }
              setSelectedDates({ checkIn: newCheckIn, checkOut: newCheckOut });
            }}
            moveRangeOnFirstSelection={false}
            rangeColors={[calendarColor]}
            minDate={new Date()} // 🔹 disables past dates
            showDateDisplay={false} // cleaner UI
            months={2} // show 2 months so cross-month ranges (e.g. 31 → 4) need no arrow
            direction="horizontal"
          />
          <div className="flex justify-end p-3 border-t border-gray-200">
            <button
              onClick={() => {
                setShowCalendar(false);
                if (typeof onApply === 'function') onApply();
              }}
              className="calendar-done-btn px-4 py-2 text-sm font-semibold rounded-lg hover:opacity-90 transition-colors"
              style={{ backgroundColor: calendarColor || '#b02e36' }}
            >
              {t('search.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
